import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { FiEdit3, FiPlus, FiTrash2, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { toast } from 'react-toastify';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import {
  listLocalChallengeIds, submitQbaseChallenge, fetchReviewCriteria, QBASE_TEXT_LIMIT,
  listChallengeSummary, listMyChallengeGroups, setChallengeScope, getChallengeScope,
} from '../../services/livesqlRepo';
import { parseUsrids, scopeProblem } from '../../utils/challengeScope';
import { loadChallengeForEdit, wrapChallengeDoc, downloadExport } from '../../services/challengeFile';
import {
  headerForm, headerPatch, newChallengeId, quizModel, newQuiz, changeQuizType, nextChoiceId,
  quizzesToExchange, validateEditor, validateQuiz, editorToExchange,
  isTuningQuiz, taskOfModel, taskSignature, unverifiedTuning, qbaseDate, qbaseTooLong, reviewInputOf, applyReviewFixes,
  cloudVerdictToReview, applyCloudFix,
  LEVEL_CODES, QUIZ_TYPES_BY_KIND, MAX_QUIZZES, SCOPES, STATUSES,
} from '../../utils/challengeEdit';
import { CHALLENGE_KINDS, CHALLENGE_DBMS } from '../../utils/challengeAttr';
import { formatApiError } from '../../utils/formatters';
import { verifyTuningTask } from '../../services/tuningGrade';
import { reviewChallenge } from '../../services/assistantClient';
import { AI_ENABLED } from '../../config/features';
import { loadSection } from '../../utils/appSettings';
import ChallengeReviewPanel from './ChallengeReviewPanel';
import useQbaseMaintenance from '../../hooks/useQbaseMaintenance';
import './ChallengeEditor.css';

const LEVEL_LABEL = { '01': 'Beginner', '02': 'Intermediate', '03': 'Expert' };

/**
 * 챌린지 편집기 — 챌린지 **등록·수정**과 그 문항(퀴즈/과제) 등록·수정·삭제(2026-10-05).
 *
 * 누구나 연다. 저장할 곳은 셋이고 형식은 하나(교환 파일 형식, utils/challengeEdit):
 *  - JSON 파일로 저장 — 내보내기와 같은 형식이라 다른 곳에서 가져오기로 넣는다.
 *  - 가져온 것에 저장 — 가져오기와 같은 경로(`onImportDoc`). 같은 번호면 문항까지 통째로 바뀐다.
 *  - QBase 에 저장 — **LSADMIN 만, 머리 정보만**(바뀐 칸만). 문항 저장 API 가 없어 문항이 바뀌었으면 막는다 —
 *    머리만 가고 문항이 조용히 남으면 「저장했다」가 거짓이 된다.
 */
function ChallengeEditor({ open, mode, challengeId, source, canSaveToQbase, currentUsrid, defaults, seed, onClose, onImportDoc, onQbaseSaved }) {
  const { t, i18n } = useTranslation();
  const qbaseMaint = useQbaseMaintenance();
  // 「AI 검토」(본인 LLM, 2026-10-06) — 결과와 진행 중 표시
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const confirm = useConfirm();
  const isNew = mode === 'new';
  const isQbase = source !== 'local';

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [orig, setOrig] = useState(null);           // 교환 형식의 원래 챌린지(새 챌린지면 null)
  const [form, setForm] = useState(() => headerForm(null));
  const [models, setModels] = useState([]);
  const [sel, setSel] = useState(0);
  const [initialJson, setInitialJson] = useState('');   // 닫을 때 「버릴까요」 판정
  const [initialQuizJson, setInitialQuizJson] = useState('');   // QBase 저장(머리만) 가능 판정
  const [existingIds, setExistingIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  // 클라우드 판정(submitChallenge 의 fix · reject) — 패널에 보이고, fix 는 받아들이면 직전 제출에 얹어 fixToken 과 다시 낸다
  const [cloudVerdict, setCloudVerdict] = useState(null);   // { review, submitted, fixToken, receivedAt }
  // 튜닝 과제 사전 검증(2026-10-06) — 내용 서명별 결과. 처음 연 그대로인 과제(initialSigs)는 다시 묻지 않는다
  const [verifs, setVerifs] = useState({});
  const [initialSigs, setInitialSigs] = useState(() => new Set());
  /**
   * 공개 범위(클라우드, 2026-10-07) — 전체 / 그룹 / 지정 사용자 / 비공개. submit 이 아니라 QBase `setChallengeScope` 로만 바뀐다.
   * touched 가 아니면 부르지 않는다 — 기존 챌린지의 그룹·사용자 대상은 조회할 길이 없어(QBase 에 요청함) 손대지 않은 채 부르면
   * 대상이 통째로 지워진다. 새 챌린지는 비공개로 만들어지므로 비공개가 아닐 때만 부른다.
   */
  const [scope, setScope] = useState({ code: 'PRIVATE', groupIds: [], usrids: '', touched: false, known: true });
  const [myGroups, setMyGroups] = useState(null);
  // 그룹 공개를 보여 줄 때 내 그룹을 읽는다(기존 챌린지가 이미 그룹 공개인 경우 포함)
  useEffect(() => {
    if (!open || !isQbase || scope.code !== 'GROUP' || myGroups !== null) return;
    listMyChallengeGroups().then(setMyGroups).catch(() => setMyGroups([]));
  }, [open, isQbase, scope.code, myGroups]);

  // 오류 문구용 t — 언어가 바뀌었다고 편집 중인 것을 다시 읽으면 안 되므로 의존성에서 뺀다
  const tRef = useRef(t);
  tRef.current = t;

  // 열 때마다 새로 읽는다 — 앞서 연 챌린지의 편집이 남으면 엉뚱한 챌린지에 저장된다
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const start = (c) => {
      const f = headerForm(c, { challengeId: newChallengeId(), kind: defaults?.kind, dbms: defaults?.dbms, author: defaults?.author });
      const ms = c ? (c.quizzes || []).map(quizModel) : [newQuiz((defaults?.kind) === 'tuning' ? '003' : '001')];
      setOrig(c); setForm(f); setModels(ms); setSel(0); setPreview(false); setVerifs({}); setCloudVerdict(null); setReview(null);
      setInitialJson(JSON.stringify(editorToExchange(f, c, ms)));
      setInitialQuizJson(JSON.stringify(quizzesToExchange(ms, f.dbms)));
      setInitialSigs(new Set(c ? ms.filter(isTuningQuiz).map(m => taskSignature(m, f.dbms)) : []));
    };
    /**
     * 에이전트 초안(seed, 교환 형식 — show_challenge_draft → SSE challenge_draft)으로 새 챌린지를 연다.
     * 새 챌린지라 번호는 새로 짓고 상태는 작성중. 「처음 상태」 는 빈 새 챌린지로 둔다 — 초안 그대로 닫으려 해도 버릴지 묻는다.
     * 튜닝 과제는 전부 검증 대상이다(initialSigs 비움) — 에이전트가 낸 과제를 재지 않고 저장하게 두지 않는다.
     */
    const startFromSeed = (sd) => {
      const base = headerForm(null, { challengeId: newChallengeId(), kind: defaults?.kind, dbms: defaults?.dbms, author: defaults?.author });
      const fromSeed = headerForm({ ...sd, challengeId: undefined }, { kind: base.kind, dbms: base.dbms });
      const f = { ...base, ...fromSeed, challengeId: base.challengeId, author: base.author, status: 'draft' };
      const ms = (sd.quizzes || []).map(quizModel);
      setOrig(null); setForm(f); setModels(ms.length ? ms : [newQuiz(f.kind === 'tuning' ? '003' : '001')]);
      setSel(0); setPreview(false); setVerifs({}); setCloudVerdict(null); setReview(null);
      setInitialJson(JSON.stringify(editorToExchange(base, null, [])));
      setInitialQuizJson('');
      setInitialSigs(new Set());
    };
    setLoadError('');
    setScope({ code: 'PRIVATE', groupIds: [], usrids: '', touched: false, known: true });
    // 기존 클라우드 챌린지 — 지금 공개 범위와 대상(작성자·관리자만 조회된다). 못 읽으면(403 등) 집계 목록의 범위만 — 대상은 모른다(known:false)
    if (isQbase && !isNew && challengeId) {
      getChallengeScope(challengeId)
        .then((sc) => {
          if (!alive) return;
          setScope({ code: sc.code, groupIds: sc.groups.map(g => g.id), usrids: sc.users.map(u => u.usrid).join(', '), touched: false, known: true, groupNames: sc.groups });
        })
        .catch(() => listChallengeSummary()
          .then((rows) => {
            const row = rows.find(r => String(r.qryChallengeId) === String(challengeId));
            if (alive && row) setScope({ code: row.openScopeCd, groupIds: [], usrids: '', touched: false, known: !['GROUP', 'USER'].includes(row.openScopeCd) });
          })
          .catch(() => {}));
    }
    if (isNew) {
      if (seed) startFromSeed(seed); else start(null);
      listLocalChallengeIds().then(ids => { if (alive) setExistingIds(ids); }).catch(() => {});
    } else {
      setLoading(true);
      loadChallengeForEdit(challengeId)
        .then(c => { if (alive) start(c); })
        .catch(e => { if (alive) setLoadError(formatApiError(e, tRef.current)); })
        .finally(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
    // seed 는 nonce 로 바뀐다 — 같은 초안을 다시 띄워도 새로 연다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNew, challengeId, defaults?.kind, defaults?.dbms, defaults?.author, seed?.nonce]);

  const current = useMemo(() => editorToExchange(form, orig, models), [form, orig, models]);
  const dirty = !loading && !loadError && JSON.stringify(current) !== initialJson;
  const problem = validateEditor(form, models, { isNew, existingIds });
  const quizChanged = JSON.stringify(current.quizzes) !== initialQuizJson;
  const patch = useMemo(() => (isNew || !orig ? {} : headerPatch(orig, form)), [isNew, orig, form]);

  const requestClose = useCallback(async () => {
    if (dirty) {
      const ok = await confirm({
        title: t('challenge:edit.discardTitle'),
        message: t('challenge:edit.discardMsg'),
        confirmText: t('challenge:edit.discard'),
        variant: 'danger',
      });
      if (!ok) return;
    }
    onClose();
  }, [dirty, confirm, onClose, t]);

  if (!open) return null;

  // ── 편집 ──
  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const q = models[sel];
  const setQ = (patchQ) => setModels(ms => ms.map((m, i) => (i === sel ? { ...m, ...patchQ } : m)));
  const setQv = (k) => (e) => setQ({ [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const allowedTypes = QUIZ_TYPES_BY_KIND[form.kind] || [];

  const addQuiz = () => {
    if (models.length >= MAX_QUIZZES) return;
    setModels(ms => [...ms, newQuiz(allowedTypes[0] || '001')]);
    setSel(models.length);
  };
  const removeQuiz = async () => {
    const ok = await confirm({
      title: t('challenge:edit.removeQuizTitle'),
      message: t('challenge:edit.removeQuizMsg', { n: sel + 1 }),
      confirmText: t('common:ui.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    setModels(ms => ms.filter((_, i) => i !== sel));
    setSel(s => Math.max(0, s - 1));
  };
  const moveQuiz = (d) => {
    const to = sel + d;
    if (to < 0 || to >= models.length) return;
    setModels(ms => { const next = [...ms]; [next[sel], next[to]] = [next[to], next[sel]]; return next; });
    setSel(to);
  };
  const setChoice = (ci, patchC) => setQ({
    choices: q.choices.map((c, i) => {
      if (i === ci) return { ...c, ...patchC };
      // 정답은 하나 — 하나를 고르면 나머지는 푼다
      return patchC.isAnswer ? { ...c, isAnswer: false } : c;
    }),
  });

  // ── 「AI 검토」 — 본인 계정으로 적정성 검토(등록 절차 1단계, 선택) ──
  // 클라우드의 공용 판정과 같은 기준(backend challenge/reviewCriteria.js)이다. 반영은 사용자가 패널에서 고른다
  const runReview = async () => {
    setReviewing(true);
    try {
      // 웹 — 클라우드 판정과 같은 기준(QBase 가 내주는 시스템 프롬프트 전문)으로 본다. 스탠드얼론은 LiveSQL 의 기준(reviewCriteria.js)
      let criteria;
      if (isQbase) {
        const c = await fetchReviewCriteria();
        criteria = c && typeof c.criteria === 'string' ? c.criteria : undefined;
      }
      const r = await reviewChallenge({
        challenge: reviewInputOf(form, models),
        criteria,
        provider: (loadSection('assistant') || {}).provider || undefined,
        lang: String((i18n && i18n.language) || 'ko').startsWith('en') ? 'en' : 'ko',
      });
      setReview({ ...r, receivedAt: Date.now() });
    } catch (e) {
      toast.error(t('challenge:review.failed', { message: formatApiError(e, t) }));
    } finally {
      setReviewing(false);
    }
  };
  const applyReview = (picks) => {
    const next = applyReviewFixes(form, models, review, picks);
    setForm(next.form);
    setModels(next.models);
    setSel(0);
    setReview(null);
    toast.success(t('challenge:review.applied'));
  };

  // ── 튜닝 과제 사전 검증 ──
  const pendingVerify = unverifiedTuning(models, form.dbms, initialSigs, verifs);
  const verifyOne = async (m) => {
    const sig = taskSignature(m, form.dbms);
    setVerifs(v => ({ ...v, [sig]: { status: 'running' } }));
    try {
      const res = await verifyTuningTask(taskOfModel(m), form.dbms);
      setVerifs(v => ({ ...v, [sig]: { status: res.ok ? 'ok' : 'fail', result: res } }));
      // 잰 원래 블록을 「개선 전」 표시값으로 — 비어 있을 때만(사람이 적은 값은 덮지 않는다)
      if (res.baselineBuffers != null) {
        setModels(ms => ms.map(x => (x.key === m.key && x.baselineBuffers === '' ? { ...x, baselineBuffers: String(res.baselineBuffers) } : x)));
      }
    } catch (e) {
      setVerifs(v => ({ ...v, [sig]: { status: 'error', message: formatApiError(e, t) } }));
    }
  };
  const verifyAll = async () => {
    // 하나씩 — 인덱스 과제는 서버에서도 테이블별로 줄을 선다. 한꺼번에 보내도 빨라지지 않고 DB 만 붐빈다
    for (const i of pendingVerify) {
      const m = models[i];
      // eslint-disable-next-line no-await-in-loop
      if (m && !validateQuiz(m)) await verifyOne(m);
    }
  };
  const verifyText = (st) => {
    if (!st) return null;
    if (st.status === 'running') return t('challenge:edit.verify.running');
    if (st.status === 'error') return t('challenge:edit.verify.error', { message: st.message });
    const res = st.result || {};
    const measured = t('challenge:edit.verify.measured', {
      base: res.baselineBuffers ?? '-', model: res.modelBuffers ?? '-', gain: res.gain ?? '-',
      modelScore: res.modelScore, unchangedScore: res.unchangedScore,
    });
    if (st.status === 'ok') return `${t('challenge:edit.verify.ok')} — ${measured}`;
    const why = (res.problems || []).map(pr => t(`challenge:edit.verify.problems.${pr.code}`, {
      reason: pr.reason || res.reason || '', score: pr.score, gain: pr.gain, min: pr.minGain,
    })).join(' ');
    return `${t('challenge:edit.verify.fail')} — ${why} (${measured})`;
  };

  // ── 저장 ──
  const run = async (fn) => {
    if (problem || saving) return;
    setSaving(true);
    try {
      await fn();
    } catch (e) {
      const status = e?.status ?? e?.response?.status;
      // 403 남의 챌린지 · 429 공용 LLM 하루 한도 · 502 모델 오류(다시 내면 된다) — 그 밖은 서버 문구 그대로
      toast.error(status === 403 ? t('challenge:edit.forbidden')
        : status === 429 ? t('challenge:edit.cloudLimit')
        : status === 502 ? t('challenge:edit.cloudModelError')
        : formatApiError(e, t));
    } finally {
      setSaving(false);
    }
  };
  const saveFile = () => run(async () => {
    downloadExport(wrapChallengeDoc(current), `livesql-challenge-${current.challengeId}`);
    setInitialJson(JSON.stringify(current));
    toast.success(t('challenge:edit.fileSaved'));
  });
  const saveLocal = () => run(async () => {
    if (pendingVerify.length) return;
    await onImportDoc(wrapChallengeDoc(current));
    onClose();
  });
  // 클라우드 저장(2026-10-06 — 문항까지). 막는 까닭을 하나씩 말한다:
  //   작성중은 저장하지 않는다 — 클라우드 목록은 브라우저가 직접 읽어 LiveSQL 이 작성자 외에게 숨길 수 없다
  //   튜닝 과제는 「가져온 것」과 같은 검증 게이트 · 칸 길이(varchar 4000)는 넘으면 DB 오류(500)라 먼저 막는다
  const tooLong = isQbase ? qbaseTooLong(current.quizzes, QBASE_TEXT_LIMIT) : [];
  // 누가 클라우드에 저장하나(사용자 결정 2026-10-06: 일반 사용자도 등록·공유) — 새 챌린지는 누구나, 있는 것은 작성자나 관리자.
  // 화면 판정은 길 안내이고 권위는 QBase 의 소유 검사(403)다. 작성중도 클라우드에 비공개로 저장하고 목록에서 작성자 외에게 숨긴다
  // (화면 숨김 — 서버 조회 조건은 QBase 에 요청함)
  const mine = isNew || (!!currentUsrid && String(orig?.mgrUsrid || '') === String(currentUsrid));
  const qbaseBlocked = !isQbase ? 'na'
    : !(canSaveToQbase || mine) ? 'role'
    // QBase 점검(쓰기 동결) 중 — 눌러도 503 이다. 입력은 그대로 남고 점검이 끝나면 단추가 다시 켜진다
    : qbaseMaint.active ? 'maintenance'
    // 클라우드 목록은 시작일 내림차순이라 빈 날짜는 맨 뒤로 간다 — 서버는 검사하지 않으니 여기서 필수(QBase 확인 2026-10-06)
    : (!form.bgngDt || !form.endDt) ? 'dates'
    : pendingVerify.length ? 'verify'
    : tooLong.length ? 'tooLong'
    : (isQbase && scopeProblem(scope)) ? 'scope'
    : (!isNew && !quizChanged && !Object.keys(patch).length && !scope.touched) ? 'unchanged'
    : null;
  /**
   * 클라우드 제출(submitChallenge, 2026-10-06) — 머리 + 문항을 한 번에. 내용이 바뀌면 QBase 가 공용 LLM 으로 판정한다:
   *   pass · notRequired → 저장됨 / fix → 보완안을 보여 주고, 받아들이면 직전 제출에 얹어 fixToken 과 다시 낸다(재판정 없이 저장) /
   *   reject → 사유를 보여 주고 고쳐서 다시 내게 한다. 새 챌린지는 머리 전체, 있는 챌린지는 바뀐 칸만(+ 문항 전체).
   */
  const submittedOf = () => {
    const fix = (h) => ({
      ...h,
      ...(h.bgngDt ? { bgngDt: qbaseDate(h.bgngDt, 'begin') } : {}),
      ...(h.endDt ? { endDt: qbaseDate(h.endDt, 'end') } : {}),
    });
    if (isNew) {
      const { quizzes: _q, ...head } = current;
      return { id: null, header: fix(head), quizzes: current.quizzes };
    }
    return { id: current.challengeId, header: fix(patch), quizzes: current.quizzes };
  };
  /** 저장 뒤 공개 범위 — 바꾼 경우만(새 챌린지는 비공개가 아닐 때만). 실패해도 내용은 이미 저장됐다 — 그 사실과 함께 말한다 */
  const applyScope = async (id) => {
    const needed = scope.touched && (!isNew || scope.code !== 'PRIVATE');
    if (!needed || !id) return;
    try {
      await setChallengeScope({ challengeId: id, scope: scope.code, groupIds: scope.groupIds, usrids: parseUsrids(scope.usrids) });
      toast.success(t('challenge:scope.saved', { scope: t(`challenge:scope.codes.${scope.code}`) }));
    } catch (e) {
      toast.error(t('challenge:scope.failed', { message: formatApiError(e, t) }));
    }
  };
  const finishSubmit = async (res, submitted) => {
    if (res && (res.verdict === 'pass' || res.verdict === 'notRequired')) {
      toast.success(submitted.id
        ? t('challenge:edit.savedQbase')
        : t('challenge:edit.createdQbase', { id: res.qryChallengeId }));
      await applyScope(submitted.id || res.qryChallengeId);
      setCloudVerdict(null);
      onQbaseSaved?.();
      onClose();
      return;
    }
    setCloudVerdict({ review: cloudVerdictToReview(res), submitted, fixToken: res && res.fixToken, receivedAt: Date.now() });
  };
  const saveQbase = () => run(async () => {
    // 공개 범위만 바꿨으면 내용은 다시 내지 않는다(판정도 필요 없다) — setChallengeScope 만
    if (!isNew && !quizChanged && !Object.keys(patch).length && scope.touched) {
      await applyScope(current.challengeId);
      onQbaseSaved?.();
      onClose();
      return;
    }
    const submitted = submittedOf();
    await finishSubmit(await submitQbaseChallenge(submitted), submitted);
  });
  const acceptCloudFix = (picks) => run(async () => {
    const { review: rv, submitted, fixToken } = cloudVerdict;
    const next = applyCloudFix(submitted, current, rv.fixes, picks);
    const resubmit = { ...submitted, header: next.header, quizzes: next.quizzes };
    await finishSubmit(await submitQbaseChallenge({ ...resubmit, acceptFix: fixToken }), resubmit);
  });
  const problemText = problem
    ? (problem.where === 'quiz'
      ? t('challenge:edit.quizProblem', { n: problem.index + 1, message: t(`challenge:edit.qerr.${problem.key}`) })
      : t(`challenge:edit.err.${problem.key}`))
    : '';

  return (
    <AppModal
      open={open}
      onClose={requestClose}
      title={isNew ? t('challenge:edit.titleNew') : t('challenge:edit.title')}
      windowed
      icon={<FiEdit3 size={14} />}
      className="app-modal-default chal-editor"
    >
      {loading && <div className="chal-editor-msg">{t('challenge:edit.loading')}</div>}
      {loadError && <div className="chal-editor-msg chal-editor-error">{loadError}</div>}
      {!loading && !loadError && (
        <>
          <div className="chal-editor-body">
            {/* 머리 정보 */}
            <section className="chal-editor-header">
              <div className="chal-editor-source">
                {t(isQbase && !isNew ? 'challenge:edit.sourceQbase' : 'challenge:edit.sourceLocal')}
              </div>
              <div className="chal-editor-grid">
                <label>
                  <span>{t('challenge:edit.id')}</span>
                  <input value={form.challengeId} onChange={setF('challengeId')} disabled={!isNew} maxLength={40} />
                </label>
                <label className="chal-editor-wide">
                  <span>{t('challenge:edit.name')}</span>
                  <input value={form.challengeNm} onChange={setF('challengeNm')} maxLength={200} />
                </label>
                <label>
                  <span>{t('challenge:edit.begin')}</span>
                  <input type="date" value={form.bgngDt} onChange={setF('bgngDt')} />
                </label>
                <label>
                  <span>{t('challenge:edit.end')}</span>
                  <input type="date" value={form.endDt} onChange={setF('endDt')} />
                </label>
                <label>
                  <span>{t('challenge:edit.level')}</span>
                  <select value={form.challengeLevelDcd} onChange={setF('challengeLevelDcd')}>
                    {LEVEL_CODES.map(c => <option key={c} value={c}>{LEVEL_LABEL[c]}</option>)}
                  </select>
                </label>
                {/* 상태 — 「가져온 것」(로컬)은 작성중·공개·비공개, 클라우드는 작성중 + 공개 범위(전체·그룹·지정 사용자·비공개) */}
                {!isQbase ? (
                  <label>
                    <span>{t('challenge:edit.status')}</span>
                    <select value={form.status} onChange={setF('status')} title={t('challenge:edit.statusHint')}>
                      {STATUSES.map(st => <option key={st} value={st}>{t(`challenge:edit.statuses.${st}`)}</option>)}
                    </select>
                  </label>
                ) : (
                  <label>
                    <span>{t('challenge:scope.label')}</span>
                    <select
                      value={form.status === 'draft' ? 'draft' : scope.code}
                      title={t('challenge:scope.hint')}
                      onChange={(e) => {
                        const v = e.target.value;
                        // 작성중은 비공개로 둔다(작성자·관리자에게만 보인다) — 상태와 범위를 한 곳에서 고른다
                        setForm(f => ({ ...f, status: v === 'draft' ? 'draft' : (v === 'ALL' ? 'open' : 'closed') }));
                        setScope(s => ({ ...s, code: v === 'draft' ? 'PRIVATE' : v, touched: true, known: true }));
                      }}
                    >
                      <option value="draft">{t('challenge:edit.statuses.draft')}</option>
                      {['ALL', 'GROUP', 'USER', 'PRIVATE'].map(c => <option key={c} value={c}>{t(`challenge:scope.codes.${c}`)}</option>)}
                    </select>
                  </label>
                )}
                <label>
                  <span>{t('challenge:edit.kind')}</span>
                  <select value={form.kind} onChange={setF('kind')}>
                    {CHALLENGE_KINDS.map(k => <option key={k} value={k}>{t(`challenge:edit.kinds.${k}`)}</option>)}
                  </select>
                </label>
                <label>
                  <span>{t('challenge:dbms.filterLabel')}</span>
                  <select value={form.dbms} onChange={setF('dbms')}>
                    {CHALLENGE_DBMS.map(d => <option key={d} value={d}>{t(`challenge:dbms.${d}`)}</option>)}
                  </select>
                </label>
              </div>
              {/* 공개 대상 — 그룹(내가 가입한 것만 지정할 수 있다) · 지정 사용자(usrid) */}
              {isQbase && form.status !== 'draft' && scope.code === 'GROUP' && (
                <div className="chal-scope-targets">
                  <span className="chal-scope-label">{t('challenge:scope.groups')}</span>
                  {myGroups === null ? <span className="chal-scope-hint">{t('challenge:scope.loading')}</span> : null}
                  {myGroups && !myGroups.length ? <span className="chal-scope-hint">{t('challenge:scope.noGroups')}</span> : null}
                  {[...(myGroups || []),
                    ...(scope.groupNames || []).filter(x => !(myGroups || []).some(g => g.qryChallengePtcpGrpId === x.id))
                      .map(x => ({ qryChallengePtcpGrpId: x.id, ptcpGrpNm: x.name }))].map(g => (
                    <label key={g.qryChallengePtcpGrpId} className="chal-scope-check">
                      <input
                        type="checkbox" checked={scope.groupIds.includes(g.qryChallengePtcpGrpId)}
                        onChange={() => setScope(s => ({
                          ...s, touched: true, known: true,
                          groupIds: s.groupIds.includes(g.qryChallengePtcpGrpId) ? s.groupIds.filter(x => x !== g.qryChallengePtcpGrpId) : [...s.groupIds, g.qryChallengePtcpGrpId],
                        }))}
                      />
                      {g.ptcpGrpNm} <span className="chal-scope-hint">{g.qryChallengePtcpGrpId}</span>
                    </label>
                  ))}
                </div>
              )}
              {isQbase && form.status !== 'draft' && scope.code === 'USER' && (
                <div className="chal-scope-targets">
                  <span className="chal-scope-label">{t('challenge:scope.users')}</span>
                  <input
                    value={scope.usrids} placeholder={t('challenge:scope.usersPh')}
                    onChange={(e) => setScope(s => ({ ...s, usrids: e.target.value, touched: true, known: true }))}
                  />
                  <span className="chal-scope-hint">{t('challenge:scope.usersHint', { n: parseUsrids(scope.usrids).length })}</span>
                </div>
              )}
              {/* 기존 챌린지의 그룹·사용자 대상은 조회할 길이 없다 — 손대지 않으면 그대로 둔다(부르지 않는다) */}
              {isQbase && !scope.known && !scope.touched && (
                <div className="chal-scope-hint">{t('challenge:scope.unknownTargets', { scope: t(`challenge:scope.codes.${scope.code}`) })}</div>
              )}
            </section>

            {/* 「AI 검토」 결과 — 고른 보완만 반영한다 */}
            {review && (
              <ChallengeReviewPanel
                key={review.receivedAt}
                result={review}
                title={t('challenge:review.titleMine')}
                onApply={applyReview}
                onClose={() => setReview(null)}
              />
            )}

            {/* 클라우드 판정 — fix 는 받아들이면 그대로 등록, reject 는 사유를 보고 고쳐서 다시 낸다 */}
            {cloudVerdict && (
              <ChallengeReviewPanel
                key={cloudVerdict.receivedAt}
                result={cloudVerdict.review}
                title={t('challenge:review.titleCloud')}
                onApply={acceptCloudFix}
                applyLabel={t('challenge:review.applyAndSubmit')}
                onClose={() => setCloudVerdict(null)}
              />
            )}

            {/* 문항 목록 */}
            <section className="chal-editor-quizbar">
              {models.map((m, i) => (
                <button
                  key={m.key}
                  type="button"
                  className={`chal-editor-qtab${i === sel ? ' is-active' : ''}${validateQuiz(m) ? ' has-error' : ''}`}
                  onClick={() => { setSel(i); setPreview(false); }}
                  title={t(`challenge:edit.types.${m.quizTcd}`)}
                >
                  Q{i + 1}
                </button>
              ))}
              <button type="button" className="chal-editor-qtab chal-editor-qadd" onClick={addQuiz}
                disabled={models.length >= MAX_QUIZZES}
                title={models.length >= MAX_QUIZZES ? t('challenge:edit.maxQuiz', { n: MAX_QUIZZES }) : t('challenge:edit.addQuiz')}>
                <FiPlus size={13} /> {t('challenge:edit.addQuiz')}
              </button>
            </section>

            {/* 문항 편집 */}
            {q ? (
              <section className="chal-editor-quiz" key={q.key}>
                <div className="chal-editor-quiz-top">
                  <label>
                    <span>{t('challenge:edit.quizType')}</span>
                    <select value={q.quizTcd} onChange={(e) => setModels(ms => ms.map((m, i) => (i === sel ? changeQuizType(m, e.target.value) : m)))}>
                      {allowedTypes.concat(allowedTypes.includes(q.quizTcd) ? [] : [q.quizTcd]).map(tcd => (
                        <option key={tcd} value={tcd}>{t(`challenge:edit.types.${tcd}`)}</option>
                      ))}
                    </select>
                  </label>
                  <span className="chal-editor-spacer" />
                  <button type="button" className="app-btn" onClick={() => moveQuiz(-1)} disabled={sel === 0} title={t('challenge:edit.moveUp')}><FiArrowUp size={13} /></button>
                  <button type="button" className="app-btn" onClick={() => moveQuiz(1)} disabled={sel === models.length - 1} title={t('challenge:edit.moveDown')}><FiArrowDown size={13} /></button>
                  <button type="button" className="app-btn app-btn-danger" onClick={removeQuiz}><FiTrash2 size={13} /> {t('challenge:edit.removeQuiz')}</button>
                </div>

                <div className="chal-editor-field">
                  <div className="chal-editor-label">
                    <span>{t('challenge:edit.quizDc')}</span>
                    <button type="button" className="chal-editor-link" onClick={() => setPreview(p => !p)}>
                      {preview ? t('challenge:edit.editText') : t('challenge:edit.preview')}
                    </button>
                  </div>
                  {preview
                    ? <div className="chal-editor-preview"><ReactMarkdown>{q.quizDc}</ReactMarkdown></div>
                    : <textarea rows={6} value={q.quizDc} onChange={setQv('quizDc')} placeholder={t('challenge:edit.quizDcPh')} />}
                </div>
                <div className="chal-editor-field">
                  <div className="chal-editor-label"><span>{t('challenge:edit.quizHint')}</span></div>
                  <input value={q.quizHint} onChange={setQv('quizHint')} />
                </div>

                {q.quizTcd === '001' && (
                  <div className="chal-editor-field">
                    <div className="chal-editor-label"><span>{t('challenge:edit.answerSql')}</span></div>
                    <textarea className="mono" rows={6} value={q.cransSqlCn} onChange={setQv('cransSqlCn')} spellCheck={false} />
                    <div className="chal-editor-help">{t('challenge:edit.answerSqlHelp')}</div>
                  </div>
                )}

                {q.quizTcd === '002' && (
                  <div className="chal-editor-field">
                    <div className="chal-editor-label"><span>{t('challenge:edit.choices')}</span></div>
                    {q.choices.map((c, ci) => (
                      <div key={c.id} className="chal-editor-choice">
                        <label className="chal-editor-answer" title={t('challenge:edit.isAnswer')}>
                          <input type="radio" name={`ans-${q.key}`} checked={c.isAnswer} onChange={() => setChoice(ci, { isAnswer: true })} />
                          <span>{c.id}</span>
                        </label>
                        <input value={c.text} onChange={(e) => setChoice(ci, { text: e.target.value })} />
                        <button type="button" className="app-btn" onClick={() => setQ({ choices: q.choices.filter((_, i) => i !== ci) })}
                          title={t('challenge:edit.removeChoice')}><FiTrash2 size={12} /></button>
                      </div>
                    ))}
                    <button type="button" className="app-btn" onClick={() => setQ({ choices: [...q.choices, { id: nextChoiceId(q.choices), text: '', isAnswer: false }] })}
                      disabled={q.choices.length >= 8}>
                      <FiPlus size={12} /> {t('challenge:edit.addChoice')}
                    </button>
                  </div>
                )}

                {(q.quizTcd === '003' || q.quizTcd === '004') && (
                  <>
                    <div className="chal-editor-row">
                      <label>
                        <span>{t('challenge:edit.scope')}</span>
                        <select value={q.scope} onChange={setQv('scope')}>
                          {SCOPES.map(s => <option key={s} value={s}>{t(`challenge:edit.scopes.${s}`)}</option>)}
                        </select>
                      </label>
                      <label>
                        <span>{t('challenge:edit.baseline')}</span>
                        <input value={q.baselineBuffers} onChange={setQv('baselineBuffers')} inputMode="numeric" placeholder={t('challenge:edit.baselinePh')} />
                      </label>
                    </div>
                    <div className="chal-editor-help">{t('challenge:edit.tuningHelp')}</div>
                    {(() => {
                      const st = verifs[taskSignature(q, form.dbms)];
                      const untouched = initialSigs.has(taskSignature(q, form.dbms));
                      return (
                        <div className="chal-editor-verify">
                          <button type="button" className="app-btn" onClick={() => verifyOne(q)}
                            disabled={!!validateQuiz(q) || st?.status === 'running'} title={t('challenge:edit.verify.hint')}>
                            {t('challenge:edit.verify.button')}
                          </button>
                          <span className={`chal-editor-verify-msg is-${st?.status || (untouched ? 'kept' : 'none')}`}>
                            {verifyText(st) || (untouched ? t('challenge:edit.verify.kept') : t('challenge:edit.verify.needed'))}
                          </span>
                        </div>
                      );
                    })()}
                  </>
                )}

                {q.quizTcd === '003' && (
                  <>
                    <div className="chal-editor-field">
                      <div className="chal-editor-label"><span>{t('challenge:edit.slowSql')}</span></div>
                      <textarea className="mono" rows={5} value={q.slowSql} onChange={setQv('slowSql')} spellCheck={false} />
                    </div>
                    <div className="chal-editor-field">
                      <div className="chal-editor-label"><span>{t('challenge:edit.modelSql')}</span></div>
                      <textarea className="mono" rows={5} value={q.cransSqlCn} onChange={setQv('cransSqlCn')} spellCheck={false} />
                    </div>
                  </>
                )}

                {q.quizTcd === '004' && (
                  <>
                    <div className="chal-editor-field">
                      <div className="chal-editor-label"><span>{t('challenge:edit.probeSql')}</span></div>
                      <textarea className="mono" rows={5} value={q.probeSql} onChange={setQv('probeSql')} spellCheck={false} />
                    </div>
                    <div className="chal-editor-row">
                      <label>
                        <span>{t('challenge:edit.targetTable')}</span>
                        <input value={q.targetTable} onChange={setQv('targetTable')} placeholder="PR_SALES" />
                      </label>
                      <label className="chal-editor-check">
                        <input type="checkbox" checked={q.answerUnique} onChange={setQv('answerUnique')} />
                        <span>{t('challenge:edit.answerUnique')}</span>
                      </label>
                    </div>
                    <div className="chal-editor-field">
                      <div className="chal-editor-label"><span>{t('challenge:edit.answerIndexes')}</span></div>
                      <textarea className="mono" rows={3} value={q.answerIndexesText} onChange={setQv('answerIndexesText')} placeholder="PROD_ID, TIME_ID" spellCheck={false} />
                      <div className="chal-editor-help">{t('challenge:edit.answerIndexesHelp')}</div>
                    </div>
                    <div className="chal-editor-field">
                      <div className="chal-editor-label"><span>{t('challenge:edit.modelSqlOptional')}</span></div>
                      <textarea className="mono" rows={3} value={q.cransSqlCn} onChange={setQv('cransSqlCn')} spellCheck={false} />
                    </div>
                  </>
                )}
              </section>
            ) : (
              <div className="chal-editor-msg">{t('challenge:edit.err.noQuiz')}</div>
            )}
          </div>

          <footer className="chal-editor-footer">
            <div className="chal-editor-status">
              {problem
                ? <span className="chal-editor-error">{problemText}</span>
                : <span>{t('challenge:edit.note')}</span>}
              {qbaseBlocked === 'role' && <span>{t('challenge:edit.qbaseLocked')}</span>}
              {qbaseBlocked === 'dates' && <span>{t('challenge:edit.qbaseBlocked.dates')}</span>}
              {qbaseBlocked === 'tooLong' && (
                <span className="chal-editor-error">
                  {t('challenge:edit.qbaseTooLong', { list: tooLong.map(x => `Q${x.quizNo}`).join(', '), limit: QBASE_TEXT_LIMIT })}
                </span>
              )}
              {form.status === 'draft' && <span>{t('challenge:edit.draftNote')}</span>}
              {isQbase && scopeProblem(scope) && <span className="chal-editor-error">{t(`challenge:scope.err.${scopeProblem(scope)}`)}</span>}
              {!problem && pendingVerify.length > 0 && (
                <span className="chal-editor-error">
                  {t('challenge:edit.verify.pending', { list: pendingVerify.map(i => `Q${i + 1}`).join(', ') })}
                </span>
              )}
            </div>
            <div className="chal-editor-actions">
              <button type="button" className="app-btn" onClick={requestClose}>{t('common:ui.cancel')}</button>
              {AI_ENABLED && (
                <button type="button" className="app-btn" onClick={runReview} disabled={reviewing || saving || !models.length}
                  title={t('challenge:review.hint')}>
                  {reviewing ? t('challenge:review.running') : t('challenge:review.button')}
                </button>
              )}
              <button type="button" className="app-btn" onClick={saveFile} disabled={!!problem || saving} title={t('challenge:edit.saveFileHint')}>
                {t('challenge:edit.saveFile')}
              </button>
              {pendingVerify.length > 0 && (
                <button type="button" className="app-btn" onClick={verifyAll} disabled={!!problem || saving}
                  title={t('challenge:edit.verify.hint')}>
                  {t('challenge:edit.verify.all', { n: pendingVerify.length })}
                </button>
              )}
              {/* 웹(클라우드)에는 「가져온 것」 이 없다 — 저장은 클라우드와 파일뿐(사용자 결정 2026-10-06) */}
              {!isQbase && (
                <button type="button" className="app-btn app-btn-primary" onClick={saveLocal}
                  disabled={!!problem || saving || pendingVerify.length > 0} title={t('challenge:edit.saveLocalHint')}>
                  {t('challenge:edit.saveLocal')}
                </button>
              )}
              {isQbase && (
                <button type="button" className="app-btn app-btn-primary" onClick={saveQbase}
                  disabled={!!problem || saving || !!qbaseBlocked}
                  title={qbaseBlocked ? t(`challenge:edit.qbaseBlocked.${qbaseBlocked}`) : t('challenge:edit.saveQbaseHint')}>
                  {saving ? t('challenge:edit.saving') : t('challenge:edit.saveQbase')}
                </button>
              )}
            </div>
          </footer>
        </>
      )}
    </AppModal>
  );
}

ChallengeEditor.propTypes = {
  open: PropTypes.bool,
  mode: PropTypes.oneOf(['new', 'edit']),
  challengeId: PropTypes.string,
  source: PropTypes.string,
  canSaveToQbase: PropTypes.bool,
  /** 지금 사용자 — 클라우드 챌린지의 작성자(mgrUsrid)와 같으면 고칠 수 있다 */
  currentUsrid: PropTypes.string,
  defaults: PropTypes.shape({ kind: PropTypes.string, dbms: PropTypes.string, author: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
  onImportDoc: PropTypes.func.isRequired,
  onQbaseSaved: PropTypes.func,
  /** 에이전트 초안(교환 형식 챌린지 + nonce) — 새 챌린지를 이것으로 채워 연다 */
  seed: PropTypes.object,
};

export default ChallengeEditor;
